import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Dialog, DialogActions, DialogContent, DialogTitle, Button } from "@mui/material";
import { useAuth } from "../../AuthContext/AuthContext";
import axios from "../../../configApi/axiosConfig";
import { APIURL } from "../../../configApi/apiConfig";
import ClienteInfoCard from "../../GestDocumentos/GestorDocumentos/ClienteInfoCard";
import { HistorialObservacionesModal } from "../HistorialObservacionesModal";
import { fetchConsultaYNotifica, fechaHoraEcuador } from "../../Utils";
import { DOCUMENTO_IDS_BS, DOCS_QUE_REQUIEREN_COMPRA_BS, getMenuItemsBs } from "./constants/documentos-bs";
import { useClienteInfo } from "./hooks/useClienteInfo";
import { useArchivosBs } from "./hooks/useArchivosBs";
import { useUploadBs } from "./hooks/useUploadBs";
import { SidebarBs } from "./components/SidebarBs";
import { PreviewAreaBs } from "./components/PreviewAreaBs";

const formatDate = (dateString) => {
  if (!dateString) return "";
  return new Date(dateString).toLocaleDateString("es-EC", {
    day: "2-digit", month: "2-digit", year: "numeric",
  });
};

export function DocumentalBancoSolidario({ id, NumeroSolicitud, nombre, cedula }) {
  const { userData, userUsuario } = useAuth();
  const navigate = useNavigate();
  const { state } = useLocation();
  const permisos = state?.permisos || [];
  const modalRef = useRef(null);

  const [activeTab, setActiveTab] = useState("Contrato de Compra");
  const [view, setView] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [history, setHistory] = useState(false);
  const [observaciones, setObservaciones] = useState([]);

  const clientInfo = useClienteInfo();

  const {
    files, setFiles,
    filePreviews, setFilePreviews,
    completedFields2,
    filesToCorrect,
    showOnlyCorrections,
    setRefreshFiles,
    activeTabInicial,
    isGenerandoDigitales,
    idCompraBS,
    progress,
    progressColor,
  } = useArchivosBs(clientInfo);

  const {
    isUploading,
    showFileInput, setShowFileInput,
    showConfirmDeleteModal,
    observacion, setObservacion,
    handleFileChange,
    handleSubmit,
    handleOpenDeleteConfirmation,
    closeDeleteConfirmation,
    handleRemoveFile,
  } = useUploadBs({
    activeTab, files, setFiles, setFilePreviews,
    clientInfo, userData,
    showOnlyCorrections, filesToCorrect,
    setRefreshFiles,
  });

  // Inicializar tab con primer campo a corregir cuando llega del hook
  useEffect(() => {
    if (activeTabInicial) setActiveTab(activeTabInicial);
  }, [activeTabInicial]);

  const puedeVisualizarfirma = () => {
    const permiso = permisos.find((p) => p.Permisos === "VISUALIZAR FIRMA");
    return permiso?.Activo;
  };

  const fetchObservaciones = async () => {
    const numeroActivetab = DOCUMENTO_IDS_BS[activeTab] ?? null;
    try {
      const response = await axios.get(APIURL.get_observaciones(clientInfo.id, numeroActivetab));
      setObservaciones(response.data);
      setHistory(true);
    } catch (err) {
      console.error("Error al obtener observaciones:", err);
    }
  };

  const patchsolicitudWeb = async () => {
    try {
      await axios.patch(APIURL.patch_solicitudweb(clientInfo.id));
      await axios.patch(APIURL.update_soliciutd_telefonica(clientInfo.id, 2));
      await axios.post(APIURL.post_createtiemposolicitudeswebDto(), {
        idCre_SolicitudWeb: clientInfo.id,
        Tipo: 3,
        idEstadoVerificacionDocumental: 2,
        Usuario: userData.Nombre,
      });
      navigate("/ListadoSolicitud", { replace: true });
    } catch (err) {
      console.error("Error al actualizar la solicitud:", err);
    }
  };

  const handleConfirm = async () => {
    setIsModalOpen(false);
    patchsolicitudWeb();
    fetchConsultaYNotifica(id, userUsuario, {
      title: "¡Documentos listos para revisar! 🔍",
      body: `¡Hola! Ya puedes verificar todos documentos de la solicitud ${NumeroSolicitud} de ${nombre} con CI. ${cedula}. ¡Gracias! 😀 📅 Fecha: ${fechaHoraEcuador}`,
      type: "success",
      empresa: "CREDI",
      url: "",
      tipo: "analista",
    });

    if (showOnlyCorrections) {
      try {
        const resp = await axios.patch(APIURL.patch_cancelados(id), { idEstadoDocumento: 6 });
        if (resp.status === 200) setRefreshFiles((prev) => !prev);
      } catch (err) {
        console.error("Error al cancelar documentos:", err);
      }
    }
  };

  const menuItemsBs = getMenuItemsBs();
  const completedFields = menuItemsBs.filter((f) => files[f]?.length > 0);
  const pendingFields = menuItemsBs.filter((f) => !files[f]?.length);

  return (
    <div className="flex min-h-screen bg-gray-100">
      <SidebarBs
        completedFields={completedFields}
        pendingFields={pendingFields}
        filesToCorrect={filesToCorrect}
        showOnlyCorrections={showOnlyCorrections}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        files={files}
        completedFields2={completedFields2}
        progress={progress}
        progressColor={progressColor}
        isMenuOpen={isMenuOpen}
        onEnviarVerificacion={() => setIsModalOpen(true)}
        idCompraBS={idCompraBS}
      />

      <button
        onClick={() => setIsMenuOpen((prev) => !prev)}
        className="fixed top-6 left-6 md:hidden z-50 bg-blue-600 text-white p-3 rounded-full shadow-lg hover:bg-blue-700 transition duration-300"
      >
        {isMenuOpen ? "❌" : "☰"}
      </button>

      <div className="flex-1 p-2 bg-white">
        <ClienteInfoCard
          clientInfo={clientInfo}
          formatDate={formatDate}
          navigate={navigate}
          permiso={puedeVisualizarfirma()}
        />

        <PreviewAreaBs
          activeTab={activeTab}
          files={files}
          filePreviews={filePreviews}
          onToggleView={() => setView((v) => !v)}
          onDelete={handleOpenDeleteConfirmation}
          onOpenUpload={() => setShowFileInput(true)}
          onSubmit={handleSubmit}
          showHistorial={clientInfo.idEstadoVerificacionDocumental === 3}
          onHistorial={fetchObservaciones}
          faltaPrefactura={!files[activeTab]?.length && !idCompraBS && DOCS_QUE_REQUIEREN_COMPRA_BS.includes(activeTab)}
        />
      </div>

      {/* Modal subir archivo */}
      {showFileInput && (
        <div
          ref={modalRef}
          className="fixed inset-0 flex justify-center items-center bg-gray-900 bg-opacity-50 z-50"
        >
          <div className="bg-white p-6 rounded-lg shadow-lg w-96 relative">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-semibold text-gray-800">Subir Nuevo Documento</h2>
              <button onClick={() => setShowFileInput(false)} className="text-gray-500 hover:text-gray-700">❌</button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="flex flex-col">
                <label className="text-lg font-medium text-gray-700">{activeTab}</label>
                <label className="mt-2 p-3 border-2 border-dashed border-gray-300 rounded-md flex items-center justify-center cursor-pointer hover:border-blue-500 transition">
                  <span className="text-gray-500 mr-2">📁</span>
                  <span className="text-gray-600">Subir archivo</span>
                  <input
                    type="file"
                    accept=".pdf,.png,.jpg,.jpeg"
                    onChange={(e) => handleFileChange(e, activeTab)}
                    className="hidden"
                  />
                </label>
              </div>
              <div className="flex justify-center">
                <button
                  type="submit"
                  className="bg-blue-600 text-white py-2 px-6 rounded-md shadow-lg hover:bg-blue-700 transition duration-300"
                >
                  Enviar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Vista previa fullscreen */}
      {view && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div ref={modalRef} className="bg-white p-4 rounded-lg shadow-lg w-3/4 h-3/4 relative">
            <button onClick={() => setView(false)} className="absolute top-2 right-2 text-lg">❌</button>
            <iframe
              src={filePreviews[activeTab]?.[0]}
              className="w-full h-full"
              title="Vista previa del archivo"
            />
          </div>
        </div>
      )}

      <HistorialObservacionesModal
        history={history}
        setHistory={setHistory}
        observaciones={observaciones}
      />

      {showConfirmDeleteModal && (
        <Dialog open={showConfirmDeleteModal} onClose={closeDeleteConfirmation}>
          <DialogTitle>Confirmación de Eliminación</DialogTitle>
          <DialogContent>
            <p>¿Estás seguro de que deseas eliminar este archivo?</p>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeDeleteConfirmation} color="primary">Cancelar</Button>
            <Button onClick={handleRemoveFile} color="secondary">Eliminar</Button>
          </DialogActions>
        </Dialog>
      )}

      {isModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h2 className="text-lg font-semibold">
              ¿Estás seguro de enviar los documentos a verificación?
            </h2>
            <div className="mt-4 flex justify-end gap-4">
              <button
                onClick={() => setIsModalOpen(false)}
                className="bg-gray-400 text-white py-2 px-4 rounded-md hover:bg-gray-500"
              >
                Cancelar
              </button>
              <button
                onClick={handleConfirm}
                className="bg-green-600 text-white py-2 px-4 rounded-md hover:bg-green-700"
              >
                Confirmar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Overlay durante generación inicial de documentos digitales */}
      {isGenerandoDigitales && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg flex flex-col items-center gap-3">
            <svg className="animate-spin h-10 w-10 text-blue-500" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
            </svg>
            <p className="text-lg font-semibold text-gray-700">Preparando documentos...</p>
            <p className="text-sm text-gray-500">Esto solo ocurre la primera vez</p>
          </div>
        </div>
      )}

      {/* Overlay durante subida de archivo manual */}
      {isUploading && !isGenerandoDigitales && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white p-6 rounded-lg shadow-lg flex flex-col items-center">
            <svg className="animate-spin h-10 w-10 text-blue-500 mb-4" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
            </svg>
            <p className="text-lg font-semibold text-gray-700">Subiendo archivo...</p>
          </div>
        </div>
      )}
    </div>
  );
}
